import React from 'react';
import BankTilesGrid from '../../components/bankSelect/BankTilesGrid';
import BankSearchList from '../../components/bankSelect/BankSearchList';
import SearchErrorMessage from '../../components/bankSelect/SearchErrorMessage';
import moment from 'moment';
import { useBanksList } from '../../hooks/queryHooks';
import { useBankSearchState, useErrorState, useSelectedBank, useSelectedProvider } from '../../hooks/zustandHooks';
import { useDebounce } from '../../hooks/useDebounce';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../App';
import { EventTypes } from '../../utils/applicationInsights';
import { buildMailtoSupportUrl } from '../../utils/supportLinks';

const BankResultsContainer = () => {
  const { setBankSearch, bankSearch } = useBankSearchState();
  const debouncedSearch = useDebounce(bankSearch, 300);
  const { t } = useTranslation();
  const history = useHistory();
  const setError = useErrorState(state => state.setError);
  const setSelectedProvider = useSelectedProvider(state => state.setSelectedProvider);
  const setSelectedBank = useSelectedBank(state => state.setSelectedBank);
  const { isError, banks, favouriteBanks, isLoading, data } = useBanksList(debouncedSearch);

  const onClearSearch = () => setBankSearch('');

  if (isError) {
    const supportEmailSubject = 'AttoConnect Support ' + moment().toISOString();

    return (
      <SearchErrorMessage
        clearSearch={onClearSearch}
        supportLink={buildMailtoSupportUrl(supportEmailSubject)}
        supportLinkTarget={'_blank'}
      />
    );
  }

  const onSelect = (brandId: number) => {
    const bank = data?.find(d => d.brandId === brandId);
    if (!bank) {
      setError({ code: t(`No providers found for brand with brandId: {{brandId}}`, { brandId }) });
      history.push('/error');
      return;
    }

    setSelectedBank(bank);
    if (bank.providers.length > 1) return;

    const provider = bank.providers[0];
    setSelectedProvider({ ...provider });
    trackEvent({ type: EventTypes.COMPLETE_PROVIDER_SELECTION, payload: { providerId: provider?.providerId } });
  };

  const props = {
    onSelect,
    isLoading
  };

  return (
    <>
      <div tabIndex={-1} className="sr-only" role="status" aria-live="polite">
        {isLoading ? 'Loading results' : null}
      </div>
      {debouncedSearch ? (
        <BankSearchList {...props} data={banks || []} searchTerm={debouncedSearch} />
      ) : (
        <BankTilesGrid {...props} data={favouriteBanks || []} />
      )}
    </>
  );
};

export { BankResultsContainer };
export default BankResultsContainer;
